import { api } from '@xtnd/web/shared/api';
import type {
  InvoiceApiResponse,
  InvoiceTransaction,
  TransformedInvoice,
  InvoiceQueryParams,
} from '../types';

// Re-export types for backward compatibility
export type { InvoiceApiResponse, InvoiceTransaction, TransformedInvoice, InvoiceQueryParams };

class InvoiceApi {
  private readonly baseSource = '/juniper-query/transactions';

  // Optimized field selection for invoices list (excludes massive events array)
  private readonly invoiceFields = [
    // Core fields
    'juniperId',
    'status',
    'matterId',
    'matterDisplayNumber',
    'matterStatus',
    'clientName',
    'trustRequestState',
    'trustRequestId',
    'trustRequestNumber',
    'createdAt',
    'updatedAt',
    'lastSentAt',
    'emailQueued',
    'emailProcessingStatus',
    'createdByUserId',
    'documents',
    // Operational Bill fields (only what we need)
    'operationalBill.id',
    'operationalBill.number',
    'operationalBill.total',
    'operationalBill.balance',
    'operationalBill.state',
    'operationalBill.issued_at',
    'operationalBill.due_at',
    'operationalBill.created_at',
    'operationalBill.last_sent_at',
    'operationalBill.matters',
    'operationalBill.user.id',
    'operationalBill.paid',
    // Trust Request Bill fields (matching operational bill fields)
    'trustRequestBill.id',
    'trustRequestBill.number',
    'trustRequestBill.total',
    'trustRequestBill.balance',
    'trustRequestBill.paid',
    'trustRequestBill.pending',
    'trustRequestBill.credits_issued',
    'trustRequestBill.state',
    'trustRequestBill.issued_at',
    'trustRequestBill.due_at',
    'trustRequestBill.created_at',
    'trustRequestBill.updated_at',
    'trustRequestBill.matters',
    // Bill recipient emails extracted from matter contacts
    'billRecipientEmails',
    // Trust request calculation data
    'calculation_data',
    // Payment link fields
    'clioPaymentLinkId',
    'clioPaymentLinkUrl',
    'clio_payment_link',
    'clio_payment_page_qr_code'
  ].join(',');

  /**
   * Get invoices with filtering and pagination
   */
  async getInvoices(params: InvoiceQueryParams = {}): Promise<InvoiceApiResponse> {
    const queryParams = new URLSearchParams();

    // Core parameters - no live data for invoices (slow-changing data)
    queryParams.set('live', 'false');
    // Use optimized field selection to exclude events and reduce response size
    queryParams.set('fields', this.invoiceFields);
    queryParams.set('limit', (params.limit || 100).toString());

    // Pagination
    if (params.cursor) {
      queryParams.set('cursor', params.cursor);
    }

    // Sorting - map frontend fields to valid backend enum values
    const backendSortField = this.mapSortField(params.sortBy || 'createdAt');
    queryParams.set('sortField', backendSortField);
    queryParams.set('sortDirection', params.sortDirection || 'desc');

    // Filtering
    if (params.statusFilter) {
      queryParams.set('status', params.statusFilter);
    }

    if (params.searchQuery) {
      queryParams.set('search', params.searchQuery);
    }

    if (params.dateFrom) {
      queryParams.set('dateFrom', params.dateFrom);
    }

    if (params.dateTo) {
      queryParams.set('dateTo', params.dateTo);
    }

    if (params.uniqueByMatter !== undefined) {
      queryParams.set('uniqueByMatter', params.uniqueByMatter.toString());
    }

    if (params.createdByUserIds && params.createdByUserIds.length > 0) {
      queryParams.set('createdByUserIds', params.createdByUserIds.join(','));
    }

    const url = `${this.baseSource}?${queryParams.toString()}`;
    const responseData = await api.get<InvoiceApiResponse>(url);

    return responseData;
  }

  /**
   * Get a single invoice by Juniper ID
   */
  async getInvoice(juniperId: string, options?: { live?: boolean; fields?: string }): Promise<any> {
    // Support live fetch and optional field selection
    const params: string[] = [];
    params.push(`live=${options?.live ? 'true' : 'false'}`);
    if (options?.fields) {
      params.push(`fields=${encodeURIComponent(options.fields)}`);
    }
    const url = `${this.baseSource}/${juniperId}?${params.join('&')}`;

    return api.get<any>(url);
  }

  /**
   * Transform API response to match UI structure
   */
  transformInvoice(transaction: InvoiceTransaction): TransformedInvoice {
    // Only destructure fields that actually exist in the API response
    const { juniperId, status, operationalBill, trustRequestState, updatedAt, trustRequestId, trustRequestNumber, clientName, createdAt, trustRequestBill } = transaction;

    // payment doesn't exist in current API response - handle gracefully
    const payment = (transaction as any).payment;

    return {
      id: juniperId,
      xtndTrustId: juniperId,
      shortJuniperId: juniperId.split('-')[1] || juniperId, // Extract segment after JUN-
      matterId:
        // 1) Prefer explicit matterId on root if present
        (transaction.matterId && String(transaction.matterId)) ||
        // 2) Fallback to operational bill's first matter id if available
        (operationalBill?.matters && operationalBill.matters.length > 0
          ? String(operationalBill.matters[0]?.id)
          : undefined) ||
        // 3) Fallback to any event-derived matter id
        this.extractMatterIdFromEvents(transaction) ||
        'Unknown',
      matterName: this.deriveMatterName(transaction),
      matterStatus: transaction.matterStatus,
      client: clientName || 'Unknown Client',
      recipients: this.deriveRecipients(transaction),
      billRecipientEmails: transaction.billRecipientEmails, // Pass through for email sending
      openingBalance: 0, // May not be available in current API
      operationalBill: {
        id: operationalBill.id, // Include ID for Clio links
        invoiceNumber: operationalBill.number,
        issuedDate: operationalBill.issued_at?.split('T')[0] || 'Unknown', // Convert to date only
        amount: operationalBill.total,
        balance: operationalBill.balance,
        status: this.mapBillStatus(operationalBill.state),
        state: operationalBill.state || 'draft', // Raw operational bill state
        approvalDate: operationalBill.created_at?.split('T')[0] || 'Unknown', // Use created_at as fallback
        dueDate: operationalBill.due_at?.split('T')[0] || undefined, // Due date for conditional formatting
        paid: operationalBill.paid,
        // Preserve user field for filtering (operationalBill.user.id)
        user: operationalBill.user ? { id: operationalBill.user.id } : undefined
      },
      allocations: [], // May need to be derived from line items or separate API
      trustRequest: {
        id: trustRequestId || (trustRequestBill?.id ? String(trustRequestBill.id) : '') || `TRR-${juniperId.split('-')[1] || 'Unknown'}`, // TRR ID from API or fallback
        number: trustRequestNumber || trustRequestBill?.number || '', // Do not derive; leave empty when not provided so UI can show N/A
        // Only populate trust request data when trustRequestState is NOT "not_created"
        date: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.issued_at?.split('T')[0] || '') : '',
        amount: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.total || 0) : 0,
        paid: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.paid || 0) : 0,
        total: trustRequestBill?.total || 0,
        issued_at: trustRequestBill?.issued_at || '',
        due_at: trustRequestBill?.due_at || '',
        status: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.state || 'draft') : 'not_created',
        // Set trrStatus based on actual trust request state, not juniper transaction status
        trrStatus: (trustRequestState === 'not_created') ? 'Not Created' : (trustRequestState || trustRequestBill?.state || 'draft'),
        state: trustRequestState || trustRequestBill?.state || 'draft', // Raw trust request state from API
      },
      juniperStatus: status, // Main juniper transaction status
      payment: {
        destinationAccount: payment?.destinationAccount || 'Unknown',
        paymentLink: payment?.paymentLink || '',
        expiresOn: payment?.expiresOn ?? (operationalBill.due_at?.split('T')[0] ?? 'Unknown'),
      },
      status: this.mapTransactionStatus(status),
      sentOn: operationalBill.last_sent_at?.split('T')[0] ?? operationalBill.issued_at?.split('T')[0] ?? 'Unknown',
      createdAt: createdAt || operationalBill.created_at || new Date().toISOString(), // Transaction creation timestamp
      updatedAt: updatedAt || operationalBill.created_at || new Date().toISOString(), // Fallback to created_at or current time
      lastSentAt: (transaction as any).lastSentAt || undefined, // Add lastSentAt from transaction
      // Trust calculation data from calculation_data object
      calculationData: transaction.calculation_data ? {
        minimumThreshold: transaction.calculation_data.minimum_threshold,
        trustBalances: transaction.calculation_data.trust_balances,
        outstandingBalances: transaction.calculation_data.outstanding_balances,
        formula: transaction.calculation_data.formula,
        calculatedAt: transaction.calculation_data.calculated_at,
      } : undefined,
      // Extract and sanitize pdfs from documents.storedAssets.pdfs for easier access
      pdfs: transaction.documents?.storedAssets?.pdfs ? {
        invoice: transaction.documents.storedAssets.pdfs.invoice ? {
          uploadedAt: transaction.documents.storedAssets.pdfs.invoice.uploadedAt,
          filename: transaction.documents.storedAssets.pdfs.invoice.filename,
          size: transaction.documents.storedAssets.pdfs.invoice.size,
          contentType: transaction.documents.storedAssets.pdfs.invoice.contentType,
        } : undefined,
      } : undefined,
      documents: transaction.documents,
      // Payment link properties from API response
      clioPaymentLinkId: transaction.clioPaymentLinkId,
      clioPaymentLinkUrl: transaction.clioPaymentLinkUrl,
      clio_payment_link: transaction.clio_payment_link,
      clio_payment_page_qr_code: transaction.clio_payment_page_qr_code,
    };
  }

  /**
   * Transform array of transactions for UI
   */
  transformInvoices(transactions: InvoiceTransaction[]): TransformedInvoice[] {
    return transactions.map(transaction => this.transformInvoice(transaction));
  }

  /**
   * Calculate status counts from invoice data
   */
  calculateStatusCounts(invoices: TransformedInvoice[]) {
    console.log('🔢 Calculating status counts for', invoices.length, 'invoices');
    console.log('📋 Status mapping:');
    console.log('  • Alert: Stuck transactions (4+ hours) or Failed status');
    console.log('  • Juniper Email on Hold: Email Held status');
    console.log('  • Pending TRR Approval: Trust Pending Approval status');
    console.log('  • Juniper Completed: Email Sent status');
    console.log('  • Operational Pre Approval: Draft or Awaiting Approval operational bill state');

    const counts = {
      pending: 0,    // Email Held
      alert: 0,      // Stuck transactions or Failed
      trrCreated: 0, // Trust Pending Approval
      sent: 0,       // Email Sent
      operationalPreApproval: 0, // Operational bill state = draft or awaiting_approval
    };

    invoices.forEach(invoice => {
      const juniperStatus = invoice.juniperStatus;
      const operationalBillState = invoice.operationalBill?.state?.toLowerCase() || '';

      // Check alert conditions first (highest priority)
      if (this.isAlertCondition(invoice)) {
        counts.alert++;
        return; // Don't count in other categories if it's an alert
      }

      // Check operational pre-approval (draft or awaiting_approval states)
      if (operationalBillState === 'draft' || operationalBillState === 'awaiting_approval') {
        counts.operationalPreApproval++;
        return; // Don't count in other categories if it's operational pre-approval
      }

      // Regular status categorization
      if (juniperStatus.toLowerCase().includes('email held')) {
        // "Juniper Email on Hold" for Email Held status
        counts.pending++;
      } else if (juniperStatus === 'Trust Pending Approval') {
        // "Pending TRR Approval" for Trust Pending Approval status
        counts.trrCreated++;
      } else if (juniperStatus === 'Completed') {
        // "Juniper Completed" corresponds to Completed status
        counts.sent++;
      }
    });

    console.log('📊 Final status counts:', counts);
    return counts;
  }

  /**
   * Check if a transaction meets alert conditions
   */
  private isAlertCondition(invoice: TransformedInvoice): boolean {
    const status = invoice.juniperStatus;
    const updatedAt = new Date(invoice.updatedAt);
    const now = new Date();
    const fourHoursAgo = new Date(now.getTime() - (4 * 60 * 60 * 1000)); // 4 hours in milliseconds

    console.log('🚨 Checking alert condition for', invoice.shortJuniperId, {
      status,
      updatedAt: updatedAt.toISOString(),
      fourHoursAgo: fourHoursAgo.toISOString(),
      isStuck: updatedAt < fourHoursAgo
    });

    // 1. Stuck in 'Processing' - no update for more than 4 hours
    if (status === 'Processing' && updatedAt < fourHoursAgo) {
      console.log('⚠️ Alert: Stuck in Processing for', invoice.shortJuniperId);
      return true;
    }

    // 2. Explicit Failure - always an alert
    if (status === 'Failed') {
      console.log('⚠️ Alert: Failed status for', invoice.shortJuniperId);
      return true;
    }

    // 3. Trust Request Alert States - require immediate attention
    if (status === 'Trust Request Deleted') {
      console.log('⚠️ Alert: Trust Request Deleted for', invoice.shortJuniperId);
      return true;
    }

    if (status === 'Trust Request Voided') {
      console.log('⚠️ Alert: Trust Request Voided for', invoice.shortJuniperId);
      return true;
    }

    // 4. Stuck in 'Trust Request Approved' - no update for more than 4 hours
    if (status === 'Trust Request Approved' && updatedAt < fourHoursAgo) {
      console.log('⚠️ Alert: Stuck in Trust Request Approved for', invoice.shortJuniperId);
      return true;
    }

    // 5. Stuck in 'PDF Ready' - no update for more than 4 hours
    if (status === 'PDF Ready' && updatedAt < fourHoursAgo) {
      console.log('⚠️ Alert: Stuck in PDF Ready for', invoice.shortJuniperId);
      return true;
    }

    return false;
  }

  // Helper methods for data transformation
  private mapSortField(frontendField: string): string {
    // Map frontend sort fields to valid backend enum values
    const sortFieldMap: Record<string, string> = {
      'matterName': 'createdAt', // Sort by creation time since matter name isn't sortable on backend
      'client': 'createdAt', // Sort by creation time since client name isn't sortable on backend
      'trrStatus': 'status', // Map to juniper status field
      'juniperStatus': 'status', // Direct mapping
      'createdAt': 'createdAt', // Direct mapping
      'updatedAt': 'updatedAt', // Direct mapping
      'juniperId': 'juniperId', // Direct mapping
      'status': 'status' // Direct mapping
    };

    return sortFieldMap[frontendField] || 'createdAt'; // Default to createdAt for unknown fields
  }

  private mapBillStatus(state: string): string {
    // Return the raw Clio bill state as-is
    // Valid states: draft, paid, awaiting_approval, awaiting_payment, voided, deleted
    return state || 'draft';
  }

  private mapTransactionStatus(status: string): string {
    return status.toLowerCase().includes('completed') ? 'completed' : 'active';
  }

  // Helper methods for status formatting removed - handled in UI component

  // Helper method removed - alert logic now handles time-based conditions

  private deriveMatterName(transaction: InvoiceTransaction): string {
    // Use matterDisplayNumber from root level - this is the correct field
    if (transaction.matterDisplayNumber && transaction.matterDisplayNumber.trim()) {
      return transaction.matterDisplayNumber;
    }

    return 'MISSING';
  }

  private deriveRecipients(transaction: InvoiceTransaction): string {
    // Use pre-extracted bill recipient emails from transaction
    if (transaction.billRecipientEmails && transaction.billRecipientEmails.length > 0) {
      // Handle both formats: array of strings or array of objects
      const emailStrings = transaction.billRecipientEmails
        .map((recipient: any) => {
          // If recipient is a string, use it directly
          if (typeof recipient === 'string') {
            return recipient;
          }
          // If recipient is an object, extract primary_email_address
          if (typeof recipient === 'object' && recipient !== null) {
            return recipient.primary_email_address;
          }
          return null;
        })
        .filter(Boolean) as string[];
      return emailStrings.join(', ');
    }

    // Fallback to client name if no bill recipients found
    return transaction.clientName || 'No recipients';
  }

  private extractMatterIdFromEvents(_transaction: InvoiceTransaction): string | null {
    // Events are excluded from API response for performance - this is now a no-op fallback
    // Matter ID should be available directly on the transaction or in operationalBill.matters
    return null;
  }
}

export const invoiceApi = new InvoiceApi();
